import PaginationNumeric from "@shared/components/PaginationNumeric";
import theme from "@shared/utils/themeColors";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthenticatedLayout from "../../../shared/layouts/AuthenticatedLayout";
import { AddTestModal } from "../components/AddTestModal";
import { ProcessedTestCard } from "../components/ProcessedTestCard";
import { TestProcessingService } from "../services/test-processing.service";

export default function TestProcessing() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [sortOption, setSortOption] = useState("newest");

  useEffect(() => {
    setPage(1);
  }, [searchQuery, statusFilter]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["processed-tests", page, searchQuery, statusFilter, sortOption],
    queryFn: () =>
      TestProcessingService.getProcessedTests({
        page,
        searchQuery,
        statusFilter,
        sortBy: sortOption,
      }),
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnReconnect: true,
    gcTime: 0,
  });

  const handleRefresh = async () => {
    setShowAddModal(false);
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  };

  const handleViewTest = (testId: string) => {
    navigate(`/test-processing/${testId}/edit`);
  };

  const handleViewPdf = (pdfUrl: string) => {
    window.open(pdfUrl, "_blank");
  };

  return (
    <>
      <AuthenticatedLayout
        isLoading={isLoading || isRefreshing}
        error={error}
        isEmpty={!data?.tests || data.tests.length === 0}
        emptyStateProps={{
          title: "No processed tests found",
          description:
            searchQuery || statusFilter
              ? "No tests match your search criteria"
              : "Start by adding your first test",
          icon: (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-t from-gray-200 to-gray-100 mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                className={`w-8 h-8 ${theme.text.muted}`}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          ),
        }}
        pageHeader={{
          title: "Tests Processing",
          search: {
            show: true,
            placeholder: "Search tests...",
            onChange: setSearchQuery,
          },
          refresh: {
            show: true,
            onRefresh: handleRefresh,
          },
          actions: [
            {
              label: "Add Test",
              onClick: () => setShowAddModal(true),
            },
          ],
        }}
      >
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Filters and sorting */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-4">
              <div className={`text-sm ${theme.text.muted}`}>
                {data?.total
                  ? `Showing ${data.tests.length} of ${data.total} Tests`
                  : "No tests found"}
              </div>
              {/* Sort */}
              {/* <div className="text-sm">
              <span>Sort by </span>
              <DropdownSort
                align="right"
                options={[
                  { label: "Newest", value: "newest" },
                  { label: "Oldest", value: "oldest" },
                  { label: "Name (A-Z)", value: "name_asc" },
                  { label: "Name (Z-A)", value: "name_desc" },
                  { label: "Questions (High-Low)", value: "questions_desc" },
                  { label: "Questions (Low-High)", value: "questions_asc" },
                ]}
                onChange={(value) => setSortOption(value)}
              />
            </div> */}
            </div>
          </div>

          {/* Tests list */}
          <div className="space-y-2">
            {data?.tests.map((test) => (
              <ProcessedTestCard
                key={test.id}
                test={test}
                onView={() => handleViewTest(test.id)}
                onViewPdf={() => test.pdfUrl && handleViewPdf(test.pdfUrl)}
              />
            ))}
          </div>

          {/* Pagination */}
          <div className="mt-8">
            <PaginationNumeric
              currentPage={page}
              totalPages={data?.totalPages}
              onPageChange={setPage}
            />
          </div>
        </div>
      </AuthenticatedLayout>
      {/* Add Test Modal */}
      {showAddModal && (
        <AddTestModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleRefresh}
        />
      )}
    </>
  );
}
